import { supabase } from '@/lib/supabase';

/**
 * Resolves the requirement_id for a booking.
 * Uses booking.requirement_id directly, or looks up via course_id → compliance_requirements.course_link_id.
 */
export async function getRequirementIdForBooking(booking) {
  if (booking.requirement_id) return booking.requirement_id;

  if (booking.course_id && booking.project_id) {
    const { data } = await supabase
      .from('compliance_requirements')
      .select('id')
      .eq('project_id', booking.project_id)
      .eq('course_link_id', booking.course_id.toString())
      .maybeSingle();
    return data?.id || null;
  }

  return null;
}

/**
 * Adds a delegate to a booked booking, syncing all related tables.
 *
 * @param {Object} params
 * @param {Object} params.booking - The training_quotes record (needs id, project_id, delegate_names, requirement_id, course_id)
 * @param {Object} params.spaceMember - The space_members record with nested user_profiles
 * @param {string} params.createdBy - The user_profiles.id of the person performing the action
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function addDelegateToBooking({ booking, spaceMember, createdBy }) {
  try {
    const userId = spaceMember.user_id || spaceMember.user_profiles?.id;
    const displayName = spaceMember.user_profiles?.display_name ||
      `${spaceMember.user_profiles?.first_name || ''} ${spaceMember.user_profiles?.last_name || ''}`.trim() ||
      'Unknown';

    // 1. Insert booking_delegates record
    const { error: delegateError } = await supabase
      .from('booking_delegates')
      .upsert({
        training_quote_id: booking.id,
        space_member_id: spaceMember.id,
        attendance_status: 'pending',
        completion_status: 'not_started',
        created_by: createdBy,
        created_date: new Date().toISOString()
      }, {
        onConflict: 'training_quote_id,space_member_id',
        ignoreDuplicates: false
      });

    if (delegateError) throw delegateError;

    // 2. Append name to training_quotes.delegate_names
    const currentNames = booking.delegate_names || [];
    if (!currentNames.includes(displayName)) {
      const { error: namesError } = await supabase
        .from('training_quotes')
        .update({
          delegate_names: [...currentNames, displayName],
          updated_date: new Date().toISOString()
        })
        .eq('id', booking.id);

      if (namesError) console.warn('Failed to update delegate_names:', namesError);
    }

    // 3. Upsert compliance_assignments with status 'booked'
    const requirementId = await getRequirementIdForBooking(booking);
    if (requirementId && booking.project_id && userId) {
      const { error: assignError } = await supabase
        .from('compliance_assignments')
        .upsert({
          project_id: booking.project_id,
          requirement_id: requirementId,
          team_member_id: userId,
          assigned: true,
          status: 'booked',
          quote_id: booking.id
        }, {
          onConflict: 'project_id,requirement_id,team_member_id'
        });

      if (assignError) console.warn('Failed to update compliance_assignments:', assignError);
    }

    // 4. Update user_qualifications renewal_status
    if (userId && requirementId) {
      const { data: existingQual } = await supabase
        .from('user_qualifications')
        .select('id')
        .eq('user_id', userId)
        .eq('requirement_id', requirementId)
        .maybeSingle();

      if (existingQual) {
        await supabase
          .from('user_qualifications')
          .update({
            renewal_status: 'booked',
            quote_id: booking.id,
            updated_date: new Date().toISOString()
          })
          .eq('id', existingQual.id);
      }
    }

    // Also try by course_link_id for cross-space matching
    if (userId && booking.course_id) {
      await supabase
        .from('user_qualifications')
        .update({
          renewal_status: 'booked',
          quote_id: booking.id,
          updated_date: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('course_link_id', booking.course_id.toString())
        .in('renewal_status', ['quoting', 'quoted']);
    }

    return { success: true };
  } catch (error) {
    console.error('Error adding delegate to booking:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Removes a delegate from a booked booking, syncing all related tables.
 *
 * @param {Object} params
 * @param {Object} params.booking - The training_quotes record
 * @param {Object} params.bookingDelegate - The booking_delegates record with nested space_member.user_profiles
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function removeDelegateFromBooking({ booking, bookingDelegate }) {
  try {
    const userId = bookingDelegate.space_member?.user_id ||
      bookingDelegate.space_member?.user_profiles?.id;
    const displayName = bookingDelegate.space_member?.user_profiles?.display_name;

    // 1. Delete booking_delegates record
    const { error: deleteError } = await supabase
      .from('booking_delegates')
      .delete()
      .eq('id', bookingDelegate.id);

    if (deleteError) throw deleteError;

    // 2. Remove name from training_quotes.delegate_names
    if (displayName) {
      const currentNames = booking.delegate_names || [];
      const idx = currentNames.indexOf(displayName);
      if (idx !== -1) {
        const updatedNames = [...currentNames];
        updatedNames.splice(idx, 1);
        const { error: namesError } = await supabase
          .from('training_quotes')
          .update({
            delegate_names: updatedNames.length > 0 ? updatedNames : [],
            updated_date: new Date().toISOString()
          })
          .eq('id', booking.id);

        if (namesError) console.warn('Failed to update delegate_names:', namesError);
      }
    }

    // 3. Reset compliance_assignments (only for THIS booking's quote_id)
    const requirementId = await getRequirementIdForBooking(booking);
    if (requirementId && booking.project_id && userId) {
      const { error: assignError } = await supabase
        .from('compliance_assignments')
        .update({
          status: null,
          quote_id: null
        })
        .eq('project_id', booking.project_id)
        .eq('requirement_id', requirementId)
        .eq('team_member_id', userId)
        .eq('quote_id', booking.id);

      if (assignError) console.warn('Failed to reset compliance_assignments:', assignError);
    }

    // 4. Reset user_qualifications (only where quote_id matches this booking)
    if (userId) {
      const { error: qualError } = await supabase
        .from('user_qualifications')
        .update({
          renewal_status: null,
          quote_id: null,
          updated_date: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('quote_id', booking.id);

      if (qualError) console.warn('Failed to reset user_qualifications:', qualError);
    }

    return { success: true };
  } catch (error) {
    console.error('Error removing delegate from booking:', error);
    return { success: false, error: error.message };
  }
}
